import { useState } from "react";
import { get2QueryKey, useUploadProof } from "@synthesis/shared-services/external/payment/hooks";
import { Button, Modal, NoticeBase, setPromiseSnack } from "@synthesis/shared-ui";
import { useQueryClient } from "@tanstack/react-query";

import { Uploader } from "../../../../components/uploader";
import { PaymentProofFileChip } from "./payment-proof-file-chip";

const PAYMENT_PROOF_UPLOAD_SNACK_MESSAGES = {
  pending: "Выполняется загрузка...",
  fulfilled: "Подтверждение оплаты загружено",
  rejected: "Не удалось загрузить файл. Попробуйте еще раз",
} as const;

const PAYMENT_PROOF_REPLACE_SNACK_MESSAGES = {
  ...PAYMENT_PROOF_UPLOAD_SNACK_MESSAGES,
  fulfilled: "Подтверждение оплаты обновлено",
} as const;

type PaymentProofModalProps = {
  requestId: string;
  isReplacement: boolean;
  onClose: () => void;
};

export const PaymentProofModal = ({ requestId, isReplacement, onClose }: PaymentProofModalProps) => {
  const queryClient = useQueryClient();
  const uploadProofMutation = useUploadProof({
    mutation: {
      onSuccess: (updatedView) => {
        queryClient.setQueryData(get2QueryKey({ path: { id: requestId } }), updatedView);
        onClose();
      },
    },
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleSelectFiles = (files: File[]) => {
    if (uploadProofMutation.isPending) {
      return;
    }

    setSelectedFile(files[0] ?? null);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (!selectedFile) {
      return;
    }

    setPromiseSnack({
      promise: uploadProofMutation.mutateAsync({ path: { id: requestId }, body: { file: selectedFile } }),
      promiseSettings: {
        title: isReplacement ? PAYMENT_PROOF_REPLACE_SNACK_MESSAGES : PAYMENT_PROOF_UPLOAD_SNACK_MESSAGES,
      },
    });
  };

  return (
    <Modal.Root isOpen setIsOpen={handleOpenChange}>
      <Modal.Content className="flex max-h-[calc(100vh-4rem)] w-[700px] flex-col overflow-hidden">
        <Modal.Title>Подтверждение оплаты</Modal.Title>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="flex flex-col gap-6 p-8">
            <h3 className="heading-h4 text-type-common">
              Документ об оплате <span className="text-type-error align-top">*</span>
            </h3>

            <NoticeBase status="info" description="Загрузите платёжное поручение, чек или иной документ, подтверждающий оплату" />

            <div className="flex flex-col gap-3">
              <Uploader multiple={false} isViewFileList={false} onSelectFiles={handleSelectFiles} />

              {selectedFile ? (
                <PaymentProofFileChip fileName={selectedFile.name} disabled={uploadProofMutation.isPending} onRemove={handleRemoveFile} />
              ) : null}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 gap-4 px-8 pb-6 pt-2">
          <Button theme="primary" size="lg" disabled={!selectedFile || uploadProofMutation.isPending} onClick={() => void handleConfirm()}>
            Подтвердить
          </Button>
          <Button theme="secondary" size="lg" disabled={uploadProofMutation.isPending} onClick={() => handleOpenChange(false)}>
            Отмена
          </Button>
        </div>
      </Modal.Content>
    </Modal.Root>
  );
};
